import { Box, Typography } from '@mui/material';
import React, { ErrorInfo, ReactNode } from 'react';

import { Button } from '../buttons';

interface ErrorBoundaryProps {
  children: ReactNode;
  msg?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };

    this.handleRetry = this.handleRetry.bind(this);
  }

  // Catch errors in the child component and update the state
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, errorInfo: null };
  }

  // Capture the error and its stack trace
  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error in development mode
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }
    this.setState({ errorInfo });
  }

  // Method to reset the error state and retry rendering
  handleRetry() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      // Show detailed error info in development mode with retry button
      if (process.env.NODE_ENV === 'development') {
        return (
          <div
            style={{
              padding: '20px',
              color: '#d8000c',
              backgroundColor: '#ffd2d2',
            }}
          >
            <h2>Oops, something went wrong!</h2>
            <p>
              <strong>Error:</strong>{' '}
              {this.state.error && this.state.error.toString()}
            </p>
            <details style={{ whiteSpace: 'pre-wrap', marginTop: '10px' }}>
              <summary style={{ cursor: 'pointer' }}>
                Click to view stack trace
              </summary>
              <pre>
                {this.state.errorInfo
                  ? this.state.errorInfo.componentStack
                  : null}
              </pre>
            </details>
            <Button onClick={this.handleRetry}>Retry</Button>
          </div>
        );
      } else {
        // In production, show a generic fallback UI with a retry button
        return (
          <Box>
            <Typography
              variant="h5"
              sx={{ fontSize: '1.2rem', fontWeight: 750, my: 1 }}
            >
              {this.props.msg ||
                'Something went wrong. Please try again later.'}
            </Typography>
            <Button onClick={this.handleRetry}>Retry</Button>
          </Box>
        );
      }
    }

    // Render children components when there's no error
    return this.props.children;
  }
}

export default ErrorBoundary;
